import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InboundFaxApiService } from '@app/core/service';
import { NotificationService } from '@app/core/service/notification.service';
import { InboundFaxesTableModel } from '@app/shared/models';
import { finalize } from 'rxjs';

@Injectable()
export class PreviewFaxService {
  private errorTitle = 'Unable to Preview Fax';
  private errorText = 'There was an issue previewing the fax. Please try again later.';

  private readonly inboundFaxApiService = inject(InboundFaxApiService);
  private readonly notificationService = inject(NotificationService);
  private readonly destroyRef = inject(DestroyRef);

  public handleClick(target: InboundFaxesTableModel): void {
    target.isPreviewLoading = true;
    this.inboundFaxApiService
      .getFaxFile(target.inboundFaxId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (target.isPreviewLoading = false))
      )
      .subscribe({
        next: (file) => {
          if (file) {
            const fileType = `application/${file.fileType}`;

            const blob = this.b64toBlob(file.fileContent, fileType);
            const url = URL.createObjectURL(blob);
            const w = window.open(url, file.documentStorageLocation);

            if (w) {
              w.addEventListener('beforeunload', () => {
                URL.revokeObjectURL(url);
              });
            }
          } else {
            this.notificationService.showError(this.errorTitle, this.errorText);
          }
        },
        error: () => this.notificationService.showError(this.errorTitle, this.errorText),
      });
  }

  private b64toBlob = (b64Data: string, contentType = '', chunkSize = 512) => {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += chunkSize) {
      const slice = byteCharacters.slice(offset, offset + chunkSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
  };
}
